import { FiiWalletData, WalletOperation, WalletPropertyRow } from './types';

export type CsvCell = number | string | null | undefined;

export interface CsvSection {
  headers: string[];
  name: string;
  rows: CsvCell[][];
}

const EMPTY_VALUE = '-';

export function protectFormulaInjection(value: string): string {
  return /^\s*[=+\-@]/.test(value) ? `'${value}` : value;
}

export function parseNumericValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;

  const isNegative = /^\s*\(.*\)\s*$/.test(value);
  let normalized = value
    .trim()
    .replace(/[()\s\u00a0]/g, '')
    .replace(/[^\d,.-]/g, '');

  if (!normalized || !/\d/.test(normalized)) return null;

  const lastComma = normalized.lastIndexOf(',');
  const lastDot = normalized.lastIndexOf('.');

  if (lastComma >= 0 && lastDot >= 0) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? /\./g : /,/g;
    normalized = normalized
      .replace(thousandsSeparator, '')
      .replace(decimalSeparator, '.');
  } else if (lastComma >= 0) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;

  return isNegative ? -Math.abs(parsed) : parsed;
}

export function getDisplayedPropertyRows(
  rows: WalletPropertyRow[] = [],
): WalletPropertyRow[] {
  const displayedReferenceDate = rows[0]?.dataReferencia;

  return displayedReferenceDate
    ? rows.filter((row) => row.dataReferencia === displayedReferenceDate)
    : [];
}

export function getFiiWalletFilename(cnpj: string): string {
  const sanitizedCnpj = cnpj.replace(/[^a-zA-Z0-9]/g, '');
  return `carteira-fii-${sanitizedCnpj || 'sem-cnpj'}.csv`;
}

function valueOrEmpty(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }

  return String(value);
}

function numericOrVisibleValue(value: unknown): number | string {
  return parseNumericValue(value) ?? valueOrEmpty(value);
}

function getOperationRoute(operation?: WalletOperation | null): string {
  if (
    !operation?.market ||
    !operation.slug ||
    operation.operationId === null ||
    operation.operationId === undefined
  ) {
    return 'Operação não encontrada';
  }

  return `/operacao/${operation.market}/${operation.slug}/${operation.operationId}`;
}

function formatPercentage(value: unknown): string {
  const parsed = parseNumericValue(value);
  if (parsed === null || parsed === 0) return EMPTY_VALUE;

  return `${(parsed * 100).toLocaleString('pt-BR', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 1,
  })}%`;
}

export function buildFiiWalletSections(data: FiiWalletData): CsvSection[] {
  return [
    {
      name: 'CRI-CRA',
      headers: [
        'Securitizadora',
        'Emissão',
        'Série',
        'Quantidade',
        'Valor',
        'Ação',
      ],
      rows: data.criCra.map((row) => [
        row.institution?.fantasyName ?? 'Securitizadora inexistente',
        valueOrEmpty(row.emissaoTratada),
        valueOrEmpty(row.serieTratada),
        numericOrVisibleValue(row.quantidade),
        numericOrVisibleValue(row.valor),
        getOperationRoute(row.operation),
      ]),
    },
    {
      name: 'FII',
      headers: [
        'FII',
        'Ativo',
        'Tipo de imóvel',
        'Quantidade de cotas',
        'Valor',
        'Ação',
      ],
      rows: data.fii.map((row) => [
        valueOrEmpty(row.operation?.name ?? row.cnpjEmissor),
        valueOrEmpty(row.operation?.product),
        valueOrEmpty(row.operation?.typeOfProperty),
        numericOrVisibleValue(row.quantidade),
        numericOrVisibleValue(row.valor),
        getOperationRoute(row.operation),
      ]),
    },
    {
      name: 'Imóveis',
      headers: [
        'Nome do imóvel',
        'Endereço',
        'Área (m²)',
        'Percentual de Vacância',
        'Percentual de Inadimplência',
        'Percentual de Receitas',
      ],
      rows: data.properties.map((row) => [
        valueOrEmpty(row.nomeImovel),
        valueOrEmpty(row.endereco),
        numericOrVisibleValue(row.area),
        formatPercentage(row.percentualVacancia),
        formatPercentage(row.percentualInadimplencia),
        formatPercentage(row.percentualReceitasFii),
      ]),
    },
    {
      name: 'FIDC',
      headers: ['FIDC', 'Quantidade de cotas', 'Valor', 'Ação'],
      rows: data.fidc.map((row) => [
        valueOrEmpty(row.operation?.name),
        numericOrVisibleValue(row.quantidade),
        numericOrVisibleValue(row.valor),
        getOperationRoute(row.operation),
      ]),
    },
  ];
}

function escapeCsvCell(value: CsvCell): string {
  const safeValue = protectFormulaInjection(valueOrEmpty(value)).replace(
    /"/g,
    '""',
  );

  return `"${safeValue}"`;
}

export function buildFiiWalletCsv(sections: CsvSection[]): string {
  const nonEmptySections = sections.filter(
    (section) => section.rows.length > 0,
  );
  if (!nonEmptySections.length) throw new Error('Não há dados para exportar.');

  const csvSections = nonEmptySections.map((section) => {
    const headers = section.headers.map(escapeCsvCell).join(';');
    const rows = section.rows
      .map((row) => row.map(escapeCsvCell).join(';'))
      .join('\r\n');

    return [escapeCsvCell(section.name), headers, rows].join('\r\n');
  });

  return `\uFEFF${csvSections.join('\r\n\r\n')}`;
}
